import { LuxButton, LuxTypography } from '@gemini/fe-ds-core';
import { MultiplePatientsButtonsWrapper } from './MultiplePatientsNavigator.styles';
import { useTranslation } from 'react-i18next';
import { CSSProperties, useMemo } from 'react';
import { usePatientStore } from '@store/patientStore';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { generateSortedPatientsList } from '@utils/functions';

interface MultiplePatientsNavigatorProps {
  style?: CSSProperties;
  noPadding?: boolean;
}

export const MultiplePatientsNavigator = ({
  noPadding = false,
}: MultiplePatientsNavigatorProps) => {
  const { t } = useTranslation();
  const patients = usePatientStore(state => state.patients);
  const currentPatientIndex = useOnlineBookingStore(
    state => state.currentPatient,
  );
  const setCurrentPatient = useOnlineBookingStore(
    state => state.setCurrentPatient,
  );

  const handleBack = () => {
    if (typeof patientTrueIndex !== 'undefined') {
      setCurrentPatient(onlyTruePatients[patientTrueIndex - 1].tabKey);
    }
  };

  const handleContinue = () => {
    if (typeof patientTrueIndex !== 'undefined') {
      setCurrentPatient(onlyTruePatients[patientTrueIndex + 1].tabKey);
    }
  };

  const onlyTruePatients = useMemo(
    () => generateSortedPatientsList(patients, t),
    [patients],
  );

  const patientTrueIndex = useMemo(
    () => onlyTruePatients.find(p => p.tabKey === currentPatientIndex)?.index,
    [onlyTruePatients, currentPatientIndex],
  );

  const prevPatient = useMemo(() => {
    if (typeof patientTrueIndex !== 'undefined') {
      return onlyTruePatients[patientTrueIndex - 1];
    }
    return undefined;
  }, [patientTrueIndex, onlyTruePatients]);

  const nextPatient = useMemo(() => {
    if (typeof patientTrueIndex !== 'undefined') {
      return onlyTruePatients[patientTrueIndex + 1];
    }
    return undefined;
  }, [patientTrueIndex, onlyTruePatients]);

  // console.log('#roba', nextPatient, prevPatient);

  const justifyContentValue = useMemo(() => {
    const prevCondition =
      onlyTruePatients.length > 1 && patientTrueIndex && patientTrueIndex > 0;
    const nextCondition =
      onlyTruePatients.length > 1 &&
      patientTrueIndex &&
      patientTrueIndex < onlyTruePatients.length - 1;
    if (prevCondition && nextCondition) {
      return 'space-between';
    } else if (prevCondition) {
      return 'flex-start';
    } else {
      return 'flex-end';
    }
  }, [patientTrueIndex, onlyTruePatients.length]);

  return (
    <MultiplePatientsButtonsWrapper
      noPadding={noPadding}
      justifyContent={justifyContentValue}
      toDisplay={onlyTruePatients.length >= 2}
    >
      {typeof patientTrueIndex !== 'undefined' &&
        prevPatient?.content &&
        onlyTruePatients.length > 1 &&
        patientTrueIndex > 0 && (
          <LuxButton
            variant="ghost"
            iconStart="ChevronLeftIcon"
            onClick={handleBack}
          >
            <LuxTypography variant="textMedium">
              {t('generalCta.manage') + ' ' + prevPatient?.content}
            </LuxTypography>
          </LuxButton>
        )}
      {typeof patientTrueIndex !== 'undefined' &&
        nextPatient?.content &&
        onlyTruePatients.length > 1 &&
        patientTrueIndex < onlyTruePatients.length - 1 && (
          <LuxButton
            variant="ghost"
            iconEnd="ChevronRightIcon"
            onClick={handleContinue}
          >
            {t('generalCta.manage') + ' ' + nextPatient?.content}
          </LuxButton>
        )}
    </MultiplePatientsButtonsWrapper>
  );
};
